/* ==========================================================================
   Well Go Store — Global JavaScript
   ========================================================================== */

/* ---------- Header Scroll ---------- */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = this;
    this.announcementBar = document.querySelector('.announcement-bar-section');
    this.scrollThreshold = 50;
    this.announcementBarHeight = 0;

    this.updateHeights();
    window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
    window.addEventListener('resize', debounce(() => this.updateHeights(), 150));
  }

  updateHeights() {
    this.announcementBarHeight = this.announcementBar ? this.announcementBar.offsetHeight : 0;
    document.documentElement.style.setProperty('--announcement-bar-height', `${this.announcementBarHeight}px`);

    const headerHeight = this.header.offsetHeight;
    document.documentElement.style.setProperty('--header-total-height', `${this.announcementBarHeight + headerHeight}px`);
  }

  onScroll() {
    const scrollY = window.scrollY;

    if (scrollY > this.announcementBarHeight) {
      this.header.classList.add('header--scrolled');
      this.header.classList.add('header--announcement-hidden');
      if (this.announcementBar) {
        this.announcementBar.classList.add('announcement-bar-section--hidden');
      }
    } else {
      this.header.classList.remove('header--scrolled');
      this.header.classList.remove('header--announcement-hidden');
      if (this.announcementBar) {
        this.announcementBar.classList.remove('announcement-bar-section--hidden');
      }
    }
  }
}
customElements.define('sticky-header', StickyHeader);

/* ---------- Mobile Menu ---------- */
class MobileMenu extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('.mobile-menu__overlay');
    this.closeBtn = this.querySelector('.mobile-menu__close');

    if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

    document.querySelectorAll('[data-mobile-menu-toggle]').forEach(btn => {
      btn.addEventListener('click', () => this.open());
    });
  }

  open() {
    this.classList.add('mobile-menu--open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('mobile-menu--open');
    document.body.classList.remove('overflow-hidden');
  }
}
customElements.define('mobile-menu', MobileMenu);

/* ---------- Hero Slideshow ---------- */
class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.hero-slideshow__slide'));
    this.dots = Array.from(this.querySelectorAll('.hero-slideshow__dot'));
    this.prevBtn = this.querySelector('.hero-slideshow__arrow--prev');
    this.nextBtn = this.querySelector('.hero-slideshow__arrow--next');
    this.currentIndex = 0;
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed || '5') * 1000;
    this.autoplay = this.dataset.autoplay !== 'false';

    if (this.slides.length <= 1) return;

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i));
    });

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

    if (this.autoplay) {
      this.startAutoplay();
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
    }
  }

  goTo(index) {
    this.slides[this.currentIndex].classList.remove('hero-slideshow__slide--active');
    this.dots[this.currentIndex]?.classList.remove('hero-slideshow__dot--active');
    this.currentIndex = (index + this.slides.length) % this.slides.length;
    this.slides[this.currentIndex].classList.add('hero-slideshow__slide--active');
    this.dots[this.currentIndex]?.classList.add('hero-slideshow__dot--active');
  }

  next() { this.goTo(this.currentIndex + 1); }
  prev() { this.goTo(this.currentIndex - 1); }

  startAutoplay() {
    this.stopAutoplay();
    this.interval = setInterval(() => this.next(), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.interval) clearInterval(this.interval);
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }
}
customElements.define('hero-slideshow', HeroSlideshow);

/* ---------- Quantity Selector ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.quantity-selector__input');
    this.minusBtn = this.querySelector('[data-action="minus"]');
    this.plusBtn = this.querySelector('[data-action="plus"]');

    if (this.minusBtn) this.minusBtn.addEventListener('click', () => this.adjust(-1));
    if (this.plusBtn) this.plusBtn.addEventListener('click', () => this.adjust(1));
    if (this.input) this.input.addEventListener('change', () => this.validate());
  }

  adjust(delta) {
    const currentValue = parseInt(this.input.value) || 1;
    const min = parseInt(this.input.min) || 0;
    const max = parseInt(this.input.max) || 9999;
    const newValue = Math.min(Math.max(currentValue + delta, min), max);
    this.input.value = newValue;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  validate() {
    const min = parseInt(this.input.min) || 0;
    const max = parseInt(this.input.max) || 9999;
    let val = parseInt(this.input.value) || min;
    this.input.value = Math.min(Math.max(val, min), max);
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- Variant Selector ---------- */
class VariantSelector extends HTMLElement {
  connectedCallback() {
    this.productData = JSON.parse(
      this.closest('[data-product-json]')?.querySelector('script[type="application/json"]')?.textContent || '{}'
    );
    this.buttons = this.querySelectorAll('.variant-picker__value');
    this.buttons.forEach(btn => {
      btn.addEventListener('click', this.onVariantChange.bind(this));
    });
  }

  onVariantChange(e) {
    const button = e.currentTarget;
    const optionGroup = button.closest('.variant-picker__option');
    optionGroup.querySelectorAll('.variant-picker__value').forEach(b => {
      b.classList.remove('variant-picker__value--active');
    });
    button.classList.add('variant-picker__value--active');

    const selectedOptions = Array.from(
      this.querySelectorAll('.variant-picker__value--active')
    ).map(b => b.dataset.value);

    const variant = this.productData.variants?.find(v =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );

    if (variant) {
      this.updateVariant(variant);
    }
  }

  updateVariant(variant) {
    const form = this.closest('form');
    const variantInput = form?.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = variant.id;

    const priceEl = this.closest('.product__info')?.querySelector('.product__price');
    if (priceEl && window.Shopify?.formatMoney) {
      priceEl.innerHTML = window.Shopify.formatMoney(variant.price);
    }

    const addBtn = form?.querySelector('[data-add-to-cart]');
    if (addBtn) {
      if (variant.available) {
        addBtn.disabled = false;
        addBtn.textContent = addBtn.dataset.addText || 'Add to Cart';
      } else {
        addBtn.disabled = true;
        addBtn.textContent = addBtn.dataset.soldText || 'Sold Out';
      }
    }

    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }
}
customElements.define('variant-selector', VariantSelector);

/* ---------- Product Thumbnails ---------- */
class ProductThumbnails extends HTMLElement {
  connectedCallback() {
    this.mainImage = this.closest('.product__media')?.querySelector('.product__main-image');
    this.thumbnails = this.querySelectorAll('.product__thumbnail');

    this.thumbnails.forEach(thumb => {
      thumb.addEventListener('click', () => {
        this.thumbnails.forEach(t => t.classList.remove('product__thumbnail--active'));
        thumb.classList.add('product__thumbnail--active');
        if (this.mainImage) {
          this.mainImage.src = thumb.dataset.fullSrc;
          this.mainImage.alt = thumb.alt;
        }
      });
    });
  }
}
customElements.define('product-thumbnails', ProductThumbnails);

/* ---------- Utilities ---------- */
function debounce(fn, delay = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}
